import { useState, type FC } from 'react';
import type { Driver, Truck } from '../types';
import './DriverManagement.css';

interface DriverManagementProps {
  drivers: Driver[];
  trucks: Truck[];
  onUpdateDriver: (driverId: string, assignedTruck: string | null) => Promise<void>;
  onDeleteDriver: (driverId: string) => Promise<void>;
  onClose: () => void;
}

const DriverManagement: FC<DriverManagementProps> = ({
  drivers,
  trucks,
  onUpdateDriver,
  onDeleteDriver,
  onClose,
}) => {
  const [expandedDriver, setExpandedDriver] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('sv-SE', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeSince = (dateString: string) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just nu';
    if (minutes < 60) return `${minutes} min sedan`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} tim sedan`;
    return `${Math.floor(hours / 24)} dagar sedan`;
  };

  const handleAssignTruck = async (driverId: string, truckId: string | null) => {
    setLoading(driverId);
    try {
      await onUpdateDriver(driverId, truckId);
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteDriver = async (driverId: string) => {
    setLoading(driverId);
    try {
      await onDeleteDriver(driverId);
      setConfirmDelete(null);
    } finally {
      setLoading(null);
    }
  };

  // Find duplicate drivers (same name)
  const duplicateNames = drivers
    .map(d => d.name)
    .filter((name, index, arr) => arr.indexOf(name) !== index);

  return (
    <div className="driver-management-overlay" onClick={onClose}>
      <div className="driver-management" onClick={e => e.stopPropagation()}>
        <div className="dm-header">
          <div className="dm-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <h2>Förarhantering</h2>
            <span className="dm-count">{drivers.length} förare</span>
          </div>
          <button className="dm-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {duplicateNames.length > 0 && (
          <div className="dm-warning">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>Dubbletter hittades ({duplicateNames.length}). Överväg att ta bort gamla förare.</span>
          </div>
        )}

        <div className="dm-content">
          {drivers.length === 0 ? (
            <div className="dm-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <p>Inga förare registrerade</p>
              <span>Förare registreras automatiskt när de öppnar mobilappen</span>
            </div>
          ) : (
            <div className="dm-list">
              {drivers.map(driver => {
                const assignedTruck = trucks.find(t => t.id === driver.assignedTruck);
                const isDuplicate = duplicateNames.includes(driver.name);
                const isExpanded = expandedDriver === driver.id;
                const isLoading = loading === driver.id;
                const isConfirmingDelete = confirmDelete === driver.id;

                return (
                  <div 
                    key={driver.id} 
                    className={`dm-driver ${isExpanded ? 'expanded' : ''} ${isDuplicate ? 'duplicate' : ''}`}
                  >
                    <div 
                      className="dm-driver-header"
                      onClick={() => setExpandedDriver(isExpanded ? null : driver.id)}
                    >
                      <div className="dm-driver-avatar">
                        <span>{driver.name?.charAt(0) || '?'}</span>
                        <div className={`dm-status-dot ${driver.isTracking ? 'active' : ''}`} />
                      </div>
                      
                      <div className="dm-driver-info">
                        <div className="dm-driver-name">
                          {driver.name || 'Okänd förare'}
                          {isDuplicate && <span className="dm-duplicate-badge">Dubblett</span>}
                        </div>
                        <div className="dm-driver-meta">
                          <span className={`dm-tracking-status ${driver.isTracking ? 'tracking' : ''}`}>
                            {driver.isTracking ? 'Spårar' : 'Inaktiv'}
                          </span>
                          <span className="dm-last-update">{getTimeSince(driver.lastUpdate)}</span>
                        </div>
                      </div>

                      <div className="dm-driver-truck">
                        {assignedTruck ? (
                          <span className="dm-truck-badge">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="1" y="3" width="15" height="13" rx="2"/>
                              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                              <circle cx="5.5" cy="18.5" r="2.5"/>
                              <circle cx="18.5" cy="18.5" r="2.5"/>
                            </svg>
                            {assignedTruck.name}
                          </span>
                        ) : (
                          <span className="dm-no-truck">Ingen truck</span>
                        )}
                      </div>

                      <svg 
                        className={`dm-chevron ${isExpanded ? 'expanded' : ''}`}
                        width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </div>

                    {isExpanded && (
                      <div className="dm-driver-details">
                        <div className="dm-detail-row">
                          <span className="dm-detail-label">ID</span>
                          <span className="dm-detail-value font-mono">{driver.id}</span>
                        </div>
                        
                        {driver.batteryLevel !== null && driver.batteryLevel !== undefined && (
                          <div className="dm-detail-row">
                            <span className="dm-detail-label">Batteri</span>
                            <span className="dm-detail-value">🔋 {driver.batteryLevel}%</span>
                          </div>
                        )}
                        
                        <div className="dm-detail-row">
                          <span className="dm-detail-label">Senast sedd</span>
                          <span className="dm-detail-value">{formatDate(driver.lastUpdate)}</span>
                        </div>

                        {driver.deviceInfo && (
                          <div className="dm-detail-row">
                            <span className="dm-detail-label">Enhet</span>
                            <span className="dm-detail-value">{driver.deviceInfo}</span>
                          </div>
                        )}

                        <div className="dm-assign-section">
                          <label className="dm-assign-label">Tilldela truck</label>
                          <select
                            className="dm-truck-select"
                            value={driver.assignedTruck || ''}
                            onChange={(e) => handleAssignTruck(driver.id, e.target.value || null)}
                            disabled={isLoading}
                          >
                            <option value="">-- Ingen truck --</option>
                            {trucks.map(truck => (
                              <option key={truck.id} value={truck.id}>
                                {truck.name} {truck.plateNumber ? `(${truck.plateNumber})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="dm-actions">
                          {isConfirmingDelete ? (
                            <div className="dm-confirm-delete">
                              <span>Är du säker?</span>
                              <button 
                                className="dm-btn dm-btn-danger"
                                onClick={() => handleDeleteDriver(driver.id)}
                                disabled={isLoading}
                              >
                                {isLoading ? 'Tar bort...' : 'Ja, ta bort'}
                              </button>
                              <button 
                                className="dm-btn dm-btn-secondary"
                                onClick={() => setConfirmDelete(null)}
                              >
                                Avbryt
                              </button>
                            </div>
                          ) : (
                            <button 
                              className="dm-btn dm-btn-danger-outline"
                              onClick={() => setConfirmDelete(driver.id)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                              Ta bort förare
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverManagement;
